/**
 * Voice Conversation Engine
 *
 * Orchestrates the full-duplex voice conversation pipeline:
 *   AudioCapture → Deepgram STT → RAG server → ElevenLabs TTS → Audio playback
 *
 * Supports two interaction modes:
 *   Push-to-talk (PTT): User holds/toggles a button to speak
 *   Open-mic: Silero VAD automatically detects speech start/end
 *
 * State machine:
 *   idle → connecting → listening → processing → speaking → idle  (PTT)
 *   idle → connecting → listening → processing → speaking → listening  (open-mic, auto-resume)
 *   processing → idle  (PTT, the reply ended with nothing spoken)
 *   processing → listening  (open-mic, the reply ended with nothing spoken)
 *
 * Open-mic features:
 *   - Silero VAD v5 for automatic speech detection
 *   - Auto-resume listening after AI finishes speaking
 *   - Barge-in: speak to interrupt AI response (with AEC cooldown)
 *   - Pre-cached Deepgram tokens for instant STT connection on speech start
 *
 * This engine handles the pipeline mode only. OpenAI Realtime mode will be
 * handled by a separate OpenAIRealtimeService (Phase 3).
 */

import type {
  VoiceConversationState,
  VoiceConversationMode,
  STTConfig,
  STTEvents,
  STTProvider,
  TurnUsage,
  LocalSTTOptions,
} from '@/types/voice'
import { AudioCaptureService } from './AudioCaptureService'
import { DeepgramSTTProvider } from './DeepgramSTTProvider'
import { WhisperLocalSTTProvider } from './WhisperLocalSTTProvider'
import {
  SileroVADService,
  type VADServiceCallbacks,
  type VADServiceConfig,
} from './SileroVADService'
import { estimateDeepgramCost, estimateElevenLabsCost } from './pricingRates'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VoiceEngineConfig {
  /** STT provider: 'deepgram' (cloud) or 'whisper-local' (edge) */
  sttProvider?: 'deepgram' | 'whisper-local'
  /** STT model to use (e.g., 'nova-3' for Deepgram, 'base' for Whisper) */
  sttModel: string
  /** STT language code */
  sttLanguage: string
  /** Whether to auto-send transcript (vs populate input) */
  autoSend: boolean
  /**
   * Interaction mode, set by the hook. The engine does not read it: it decides by how
   * a turn started (startListening: the person ends the turn; the VAD: open-mic).
   */
  mode: VoiceConversationMode
  /** WebSocket URL for local STT server (when sttProvider='whisper-local') */
  localSttWsUrl?: string
  /** Open-mic: a turn that hears no words for this long lets go (ms; OPEN_MIC_NO_WORDS_MS when absent) */
  openMicNoWordsMs?: number
  /** Edge-local: the person's thresholds for the local STT server, sent on every connection */
  localStt?: LocalSTTOptions
}

export interface VoiceEngineCallbacks {
  /** State changed */
  onStateChange: (state: VoiceConversationState) => void
  /** Partial (interim) transcript update */
  onPartialTranscript: (text: string) => void
  /** Final transcript ready */
  onFinalTranscript: (text: string) => void
  /** Audio level update (0-1) for visualization */
  onAudioLevel: (level: number) => void
  /** Turn usage data available (non-blocking, after turn completes) */
  onTurnUsage: (usage: TurnUsage) => void
  /** Error occurred */
  onError: (error: Error) => void
  /** Speech detected/lost by VAD (open-mic only, for UI sub-state) */
  onSpeechDetected: (detected: boolean) => void
  /** Request TTS interruption (barge-in) */
  onInterruptTTS: () => void
}

/** What the engine uses of the microphone capture (a seam for tests). */
export interface AudioCaptureLike {
  readonly initialized: boolean
  readonly sampleRate: number
  readonly stream: MediaStream | null
  onAudioChunk?: (pcm: ArrayBuffer) => void
  onAudioLevel?: (level: number) => void
  initialize(): Promise<void>
  resume(): Promise<void>
  suspend(): Promise<void>
  destroy(): void
}

/** What the engine uses of the voice activity detector (a seam for tests). */
export interface VADLike {
  initialize(
    callbacks: VADServiceCallbacks,
    config?: VADServiceConfig,
    sharedStream?: MediaStream
  ): Promise<void>
  start(): Promise<void>
  pause(): Promise<void>
  destroy(): Promise<void>
}

/** The engine's collaborators; each defaults to the real implementation. */
export interface VoiceEngineDeps {
  createAudioCapture: () => AudioCaptureLike
  createSTTProvider: (events: STTEvents, config: VoiceEngineConfig) => STTProvider
  createVAD: () => VADLike
}

const defaultDeps: VoiceEngineDeps = {
  createAudioCapture: () => new AudioCaptureService(),
  createSTTProvider: (events, config) =>
    config.sttProvider === 'whisper-local'
      ? new WhisperLocalSTTProvider(events, config.localSttWsUrl)
      : new DeepgramSTTProvider(events),
  createVAD: () => new SileroVADService(),
}

/** Fallback submit after the person ends a turn, for a provider that may not answer Finalize (ms). */
export const FINALIZE_FALLBACK_MS = 2_000
/** Same, for a provider that always answers Finalize: wait for its answer (ms). */
export const FINALIZE_FALLBACK_ALWAYS_ANSWERS_MS = 10_000
/** A push-to-talk turn with no words for this long ends as if the person had ended it (ms). */
export const PTT_SILENCE_LIMIT_MS = 60_000

/** Audio kept from before the detector fired, so the start of a turn is not clipped */
export const PRE_ROLL_MS = 800

/** The most audio a turn holds while its connection opens */
export const TURN_QUEUE_MAX_MS = 30_000

/** An open-mic turn that hears no words for this long lets go (ms) */
export const OPEN_MIC_NO_WORDS_MS = 4_000

/** After open-mic speech ends, how long to wait for the speech service's answer (ms) */
export const SPEECH_END_TIMEOUT_MS = 2_000

/** Barge-in cooldown after TTS starts (ms). Prevents AEC false positives. */
const BARGE_IN_COOLDOWN_MS = 500

/** How often to refresh the cached Deepgram token (ms). TTL is 30s, refresh at 25s. */
const TOKEN_REFRESH_INTERVAL_MS = 25_000

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class VoiceConversationEngine {
  private audioCapture: AudioCaptureLike | null = null
  private sttProvider: STTProvider | null = null
  // The provider capture feeds directly; set only once the turn's queue is flushed
  private _liveTo: STTProvider | null = null
  // Audio held for a turn whose connection is still opening
  private _queue: { chunks: ArrayBuffer[]; ms: number; sealed: boolean } | null = null
  // The queue's length in ms when it was flushed (read by the finalize path)
  private _flushedMs = 0
  // Open-mic audio from before the detector fired, newest last
  private _preRoll: { pcm: ArrayBuffer; ms: number }[] = []
  // The first chunk after a resume is stale buffer: drop it
  private _dropNext = false
  // Capture operations run one at a time, in the order they were asked
  private _captureChain: Promise<void> = Promise.resolve()
  private vadService: VADLike | null = null
  private callbacks: VoiceEngineCallbacks
  private config: VoiceEngineConfig
  private deps: VoiceEngineDeps

  // Core state
  private _state: VoiceConversationState = 'idle'
  private _partialTranscript = ''
  private _finalTranscript = ''
  private _turnStartedAt = 0
  private _speechEndedAt = 0
  private _ttsCharacterCount = 0
  private _destroyed = false

  // Turn ownership: 'person' = started by startListening (ended by the person),
  // 'detector' = started by the open-mic VAD (ended by the speech service)
  private _turnOwner: 'person' | 'detector' | null = null
  private _ending = false
  // A stop arrived while connecting: the turn ends once connecting finishes
  private _endRequested = false
  // The release came while connecting: the queue was sealed, so the audio is only what was said before it
  private _releasedEarly = false
  // Bumped by every start, cancel, interrupt and destroy; an await that wakes to a
  // different value belongs to a turn that is over
  private _turnSeq = 0
  private _fallbackTimer: ReturnType<typeof setTimeout> | null = null
  private _silenceTimer: ReturnType<typeof setTimeout> | null = null
  // Open-mic: the no-words limit, and the wait for an answer after speech ends
  private _noWordsTimer: ReturnType<typeof setTimeout> | null = null
  private _speechEndTimer: ReturnType<typeof setTimeout> | null = null
  // The current open-mic turn's no-words limit, read from config at its speech start (ms)
  private _turnNoWordsMs = OPEN_MIC_NO_WORDS_MS

  // Open-mic state
  private _isOpenMicActive = false
  private _isSpeechDetected = false
  private _tokenFetcher: (() => Promise<string>) | null = null
  private _cachedToken: string | null = null
  private _tokenRefreshTimer: ReturnType<typeof setInterval> | null = null
  private _bargeInCooldownUntil = 0

  // LLM usage (reported by parent after RAG completes)
  private _llmUsage: {
    provider: string
    model: string
    promptTokens: number
    completionTokens: number
    totalTokens: number
    estimatedCost: number
  } | null = null

  constructor(
    callbacks: VoiceEngineCallbacks,
    config: VoiceEngineConfig,
    deps: Partial<VoiceEngineDeps> = {}
  ) {
    this.callbacks = callbacks
    this.config = config
    this.deps = { ...defaultDeps, ...deps }
  }

  get state(): VoiceConversationState {
    return this._state
  }

  get partialTranscript(): string {
    return this._partialTranscript
  }

  get isOpenMicActive(): boolean {
    return this._isOpenMicActive
  }

  get isSpeechDetected(): boolean {
    return this._isSpeechDetected
  }

  /** Update configuration (e.g., when settings change) */
  updateConfig(config: Partial<VoiceEngineConfig>): void {
    const previousLanguage = this.config.sttLanguage
    this.config = { ...this.config, ...config }

    // Mid-conversation language change requires a Deepgram WS reconnect.
    if (
      config.sttLanguage !== undefined &&
      config.sttLanguage !== previousLanguage
    ) {
      if (this.detectorTurnOpen()) {
        // An open-mic turn lets go; the next speech start opens a fresh WS with the new lang.
        this.endQuietly()
      } else if (this._state === 'idle' || this._state === 'listening') {
        // Close current provider; next utterance opens a fresh WS with new lang.
        this.closeProvider()
      }
      // else: state is 'processing' or 'speaking' — engine will pick up the
      // new language naturally when next opening a provider for the next turn.
    }
  }

  // =========================================================================
  // Push-to-Talk Mode (the person ends the turn)
  // =========================================================================

  /**
   * Start listening for speech (PTT mode).
   * Call this when user presses the voice button.
   * @param token - Ephemeral JWT from backend, or a function that fetches it (capture
   *   starts at the press while the token is still on its way)
   */
  async startListening(token: string | (() => Promise<string>)): Promise<void> {
    if (this._destroyed) return
    if (this._state !== 'idle' && this._state !== 'error') return

    const turn = ++this._turnSeq
    this._turnOwner = 'person'
    this._ending = false
    this._endRequested = false
    this._releasedEarly = false
    this.clearTurnTimers()
    this.resetTurnState()
    this.beginQueue([])
    this.setState('connecting')

    // The token is asked for at once; a rejection before it is awaited is not unhandled
    const tokenPromise = (async () => (typeof token === 'string' ? token : token()))()
    tokenPromise.catch(() => {})

    // A stale turn leaves; it suspends capture only when nothing newer owns it
    const stale = (): boolean => {
      if (turn === this._turnSeq) return false
      if (this._state === 'idle') this.suspendQuietly()
      return true
    }

    try {
      await this.ensureAudioCapture()
      if (stale()) return

      await this.startCapture()
      if (stale()) return

      const deepgramToken = await tokenPromise
      if (stale()) return

      await this.connectDeepgram(deepgramToken)
      if (stale()) return

      this.setState('listening')
      if (this._endRequested) {
        // Released before the connection was ready: submit what the queue held
        this._endRequested = false
        await this.finishPersonTurn()
      } else {
        this.armSilenceCeiling()
      }
    } catch (error) {
      // A connect closed under a cancelled turn rejects here: stay silent, but
      // still suspend capture when nothing newer owns it
      if (stale()) return
      this.closeSTT()
      this.suspendQuietly()
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      )
      this.setState('error')
    }
  }

  /**
   * Stop listening and finalize the transcript (PTT mode).
   * Call this when user releases the voice button (hold mode)
   * or clicks to stop (toggle mode).
   */
  async stopAndFinalize(): Promise<void> {
    if (this._state === 'connecting' && this._turnOwner === 'person') {
      if (this._endRequested) return
      // Seal the queue: the audio up to the release is all this turn holds
      this._endRequested = true
      this._releasedEarly = true
      if (this._queue) this._queue.sealed = true
      this._speechEndedAt = Date.now()
      this.suspendQuietly()
      return
    }
    if (this._state !== 'listening') return

    if (this._turnOwner === 'person') {
      await this.finishPersonTurn()
      return
    }

    // Only a person's turn ends here; an open-mic turn ends on its own
    return
  }

  /** End a push-to-talk turn the person ended: finalize, then submit the marked answer or the fallback. */
  private async finishPersonTurn(): Promise<void> {
    if (this._ending) return
    this._ending = true
    this.clearSilenceCeiling()
    if (!this._speechEndedAt) this._speechEndedAt = Date.now()
    try {
      await this.suspendCapture()
    } catch {
      // A failed suspend must not strand the turn: still finalize
    }
    // The turn may have been submitted, cancelled or destroyed while suspending
    if (this._destroyed || this._state !== 'listening' || !this._ending) return
    this.sttProvider?.finalize()
    // The turn is submitted by the provider's marked answer to Finalize (or an
    // UtteranceEnd, for a provider that does not always answer), else this fallback.
    let wait =
      this.sttProvider?.isConnected && this.sttProvider.alwaysAnswersFinalize
        ? FINALIZE_FALLBACK_ALWAYS_ANSWERS_MS
        : FINALIZE_FALLBACK_MS
    // The queued audio is sent at the flush, and the provider needs time to hear it
    if (this._releasedEarly) {
      wait = Math.min(wait + this._flushedMs, FINALIZE_FALLBACK_ALWAYS_ANSWERS_MS)
    }
    if (this._fallbackTimer) clearTimeout(this._fallbackTimer)
    this._fallbackTimer = setTimeout(() => {
      this._fallbackTimer = null
      if (this._state === 'listening' && this._ending) {
        this.processFinalTranscript()
      }
    }, wait)
  }

  /**
   * Interrupt AI speech.
   * In PTT mode: returns to idle.
   * In open-mic mode: returns to listening (VAD active).
   */
  interrupt(): void {
    this._turnSeq++
    this._ending = false
    this.clearTurnTimers()
    // If we're currently in speaking state, trigger TTS interruption callback
    if (this._state === 'speaking') {
      this.callbacks.onInterruptTTS?.()
    }
    this.closeSTT()
    if (this._isOpenMicActive) {
      this.setState('listening')
      this.resumeVAD()
    } else {
      // Only a push-to-talk turn owns capture; a cancel while open-mic is still connecting must leave it running
      if (this._turnOwner === 'person') this.suspendQuietly()
      this.setState('idle')
    }
  }

  /** Cancel any ongoing operation and return to idle (or listening in open-mic) */
  cancel(): void {
    this._turnSeq++
    this._ending = false
    this.clearTurnTimers()
    this.closeSTT()
    if (this._isOpenMicActive) {
      this.setState('listening')
      this.resumeVAD()
    } else {
      // Only a push-to-talk turn owns capture; a cancel while open-mic is still connecting must leave it running
      if (this._turnOwner === 'person') this.suspendQuietly()
      this.setState('idle')
    }
  }

  // =========================================================================
  // Open-Mic Mode
  // =========================================================================

  /**
   * Activate open-mic mode.
   * Initializes mic, VAD, and pre-fetches a Deepgram token.
   *
   * @param tokenFetcher - Async function that returns a fresh Deepgram ephemeral token
   */
  async startOpenMic(tokenFetcher: () => Promise<string>): Promise<void> {
    if (this._destroyed) return
    if (this._isOpenMicActive) return

    this._tokenFetcher = tokenFetcher
    // A push-to-talk turn still connecting is over; no legitimate provider is open yet
    this._turnSeq++
    this.closeSTT()
    this.clearTurnTimers()
    this._turnOwner = null
    this.setState('connecting')

    try {
      // 1. Initialize audio capture (get mic access)
      await this.ensureAudioCapture()
      await this.startCapture()

      // 2. Pre-fetch Deepgram token
      await this.refreshToken()

      // 3. Start token refresh interval (tokens expire after 30s)
      this._tokenRefreshTimer = setInterval(
        () => this.refreshToken(),
        TOKEN_REFRESH_INTERVAL_MS
      )

      // 4. Initialize VAD with shared mic stream
      this.vadService = this.deps.createVAD()
      await this.vadService.initialize(
        {
          onSpeechStart: () => this.handleVADSpeechStart(),
          onSpeechEnd: () => this.handleVADSpeechEnd(),
          onVADMisfire: () => {
            // Speech was too short — reset detection state
            if (this._isSpeechDetected) {
              this._isSpeechDetected = false
              this.callbacks.onSpeechDetected(false)
            }
          },
        },
        {
          positiveSpeechThreshold: 0.3,
          negativeSpeechThreshold: 0.25,
          redemptionMs: 1400,
          minSpeechMs: 400,
        },
        this.audioCapture!.stream ?? undefined
      )

      // 5. Start VAD listening
      await this.vadService.start()
      this._isOpenMicActive = true
      this.setState('listening')
    } catch (error) {
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      )
      this.setState('error')
    }
  }

  /**
   * Deactivate open-mic mode and return to idle.
   */
  async stopOpenMic(): Promise<void> {
    // The current turn is over, whatever is still on its way
    this._turnSeq++
    this.clearTurnTimers()
    this._turnOwner = null
    this._isOpenMicActive = false
    this._isSpeechDetected = false
    this.callbacks.onSpeechDetected(false)

    // Close any active STT connection and drop the turn's queue
    this.closeSTT()

    // Stop token refresh
    if (this._tokenRefreshTimer) {
      clearInterval(this._tokenRefreshTimer)
      this._tokenRefreshTimer = null
    }
    this._cachedToken = null
    this._tokenFetcher = null

    // Stop and destroy VAD
    if (this.vadService) {
      await this.vadService.destroy()
      this.vadService = null
    }

    this._preRoll = []

    // Suspend audio capture
    await this.suspendCapture()

    this.setState('idle')
  }

  // =========================================================================
  // TTS Bridge (called by parent component)
  // =========================================================================

  /**
   * Notify the engine that TTS has started speaking.
   * Called by the parent component when the first audio byte arrives.
   */
  notifyTTSStarted(): void {
    if (this._state === 'processing') {
      this.setState('speaking')

      // Set barge-in cooldown to let AEC calibrate before accepting VAD signals
      if (this._isOpenMicActive) {
        this._bargeInCooldownUntil = Date.now() + BARGE_IN_COOLDOWN_MS
        // Resume VAD so it can detect barge-in speech while AI is talking.
        // The cooldown above prevents false positives from TTS audio echo.
        this.resumeVAD()
      }
    }
  }

  /**
   * Notify the engine that TTS has finished speaking.
   * Called by the parent component when audio playback completes.
   *
   * In open-mic mode: auto-resumes listening (VAD active).
   * In PTT mode: returns to idle.
   *
   * Also ends a turn still in `processing`: a reply that produced no audio
   * never reaches `speaking` (SA-1402). Returns true when it ended a turn,
   * false (nothing changed) from any other state.
   */
  notifyTTSFinished(): boolean {
    if (this._state === 'processing' || this._state === 'speaking') {
      if (this._isOpenMicActive) {
        // Auto-resume: go back to listening instead of idle
        this.setState('listening')
        this.resumeVAD()
        // Pre-fetch a fresh token for the next turn
        this.refreshToken()
      } else {
        this.setState('idle')
      }
      return true
    }
    return false
  }

  /**
   * Track characters sent to TTS for cost tracking.
   * Called by the parent component for each token sent to ElevenLabs.
   */
  trackTTSCharacters(count: number): void {
    this._ttsCharacterCount += count
  }

  /**
   * Report LLM usage data (from RAG server response or future usage events).
   * Called by the parent component when RAG streaming completes.
   */
  reportLLMUsage(usage: {
    provider: string
    model: string
    promptTokens: number
    completionTokens: number
    totalTokens: number
    estimatedCost: number
  }): void {
    this._llmUsage = usage
  }

  /**
   * Build and emit TurnUsage. Called when the turn is fully complete
   * (after TTS finishes or after processing if no TTS).
   */
  emitTurnUsage(): void {
    const now = Date.now()
    const sttDurationMs = (this.sttProvider as DeepgramSTTProvider | WhisperLocalSTTProvider | null)?.totalDurationMs ?? 0
    const sttModel = (this.sttProvider as DeepgramSTTProvider | WhisperLocalSTTProvider | null)?.modelUsed ?? this.config.sttModel
    const sttProviderName = this.config.sttProvider || 'deepgram'
    const isLocalSTT = sttProviderName !== 'deepgram'

    const usage: TurnUsage = {
      turnStartedAt: this._turnStartedAt,
      turnCompletedAt: now,
      stt: {
        provider: sttProviderName,
        model: sttModel,
        audioDurationMs: sttDurationMs,
        estimatedCost: isLocalSTT ? 0 : estimateDeepgramCost(sttModel, sttDurationMs),
      },
      tts: this._ttsCharacterCount > 0
        ? {
            provider: 'elevenlabs',
            model: 'flash_v2_5',
            characterCount: this._ttsCharacterCount,
            audioDurationMs: 0,
            estimatedCost: estimateElevenLabsCost(
              'flash_v2_5',
              this._ttsCharacterCount
            ),
          }
        : undefined,
      llm: this._llmUsage
        ? {
            ...this._llmUsage,
          }
        : undefined,
      totalEstimatedCost: 0,
      latencyMs: this._speechEndedAt > 0
        ? now - this._speechEndedAt
        : undefined,
    }

    // Calculate total
    usage.totalEstimatedCost =
      (usage.stt?.estimatedCost ?? 0) +
      (usage.llm?.estimatedCost ?? 0) +
      (usage.tts?.estimatedCost ?? 0)

    this.callbacks.onTurnUsage(usage)
  }

  /** Full cleanup — releases all resources */
  destroy(): void {
    this._destroyed = true
    this._turnSeq++
    this.clearTurnTimers()

    // Stop token refresh
    if (this._tokenRefreshTimer) {
      clearInterval(this._tokenRefreshTimer)
      this._tokenRefreshTimer = null
    }

    // Destroy VAD
    this.vadService?.destroy()
    this.vadService = null

    this.closeSTT()
    this._preRoll = []
    this.audioCapture?.destroy()
    this.audioCapture = null
  }

  // =========================================================================
  // Private — State Management
  // =========================================================================

  private setState(state: VoiceConversationState): void {
    this._state = state
    this.callbacks.onStateChange(state)
  }

  /** (Re)start the push-to-talk silence ceiling: with no words for a minute, end the turn. */
  private armSilenceCeiling(): void {
    this.clearSilenceCeiling()
    this._silenceTimer = setTimeout(() => {
      this._silenceTimer = null
      if (this._turnOwner === 'person' && this._state === 'listening' && !this._ending) {
        void this.stopAndFinalize()
      }
    }, PTT_SILENCE_LIMIT_MS)
  }

  private clearSilenceCeiling(): void {
    if (this._silenceTimer) {
      clearTimeout(this._silenceTimer)
      this._silenceTimer = null
    }
  }

  /** (Re)start an open-mic turn's no-words limit; edge-local runs none. */
  private armNoWordsLimit(turn: number): void {
    if (this.config.sttProvider === 'whisper-local') return
    this.clearNoWordsLimit()
    this._noWordsTimer = setTimeout(() => {
      this._noWordsTimer = null
      if (turn === this._turnSeq && this._turnOwner === 'detector' && this._state === 'listening') {
        this.submitOrEndQuietly()
      }
    }, this._turnNoWordsMs)
  }

  private clearNoWordsLimit(): void {
    if (this._noWordsTimer) {
      clearTimeout(this._noWordsTimer)
      this._noWordsTimer = null
    }
  }

  private clearTurnTimers(): void {
    if (this._fallbackTimer) {
      clearTimeout(this._fallbackTimer)
      this._fallbackTimer = null
    }
    this.clearSilenceCeiling()
    this.clearNoWordsLimit()
    if (this._speechEndTimer) {
      clearTimeout(this._speechEndTimer)
      this._speechEndTimer = null
    }
  }

  private resetTurnState(): void {
    this._turnStartedAt = Date.now()
    this._partialTranscript = ''
    this._finalTranscript = ''
    this._ttsCharacterCount = 0
    this._llmUsage = null
    this._speechEndedAt = 0
  }

  // =========================================================================
  // Private — Audio Capture
  // =========================================================================

  private async ensureAudioCapture(): Promise<void> {
    if (!this.audioCapture) {
      this.audioCapture = this.deps.createAudioCapture()
      this.audioCapture.onAudioLevel = (level) => {
        this.callbacks.onAudioLevel(level)
      }
      // The one place microphone audio enters the engine
      this.audioCapture.onAudioChunk = (pcm) => this.routeChunk(pcm)
    }
  }

  /** Run a capture operation after the one before it has settled (resolved or rejected). */
  private captureOp(op: (capture: AudioCaptureLike) => Promise<void>): Promise<void> {
    const run = this._captureChain.then(() =>
      this.audioCapture ? op(this.audioCapture) : undefined
    )
    this._captureChain = run.then(
      () => {},
      () => {}
    )
    return run
  }

  /** Initialize capture, or resume it and drop the stale first chunk. */
  private startCapture(): Promise<void> {
    return this.captureOp(async (capture) => {
      if (!capture.initialized) {
        await capture.initialize()
      } else {
        this._dropNext = true
        await capture.resume()
      }
    })
  }

  private suspendCapture(): Promise<void> {
    return this.captureOp((capture) => capture.suspend())
  }

  /** Suspend without waiting; a failed suspend is not an error to report. */
  private suspendQuietly(): void {
    this.suspendCapture().catch(() => {})
  }

  private chunkMs(pcm: ArrayBuffer): number {
    const rate = this.audioCapture?.sampleRate ?? 16000
    return (pcm.byteLength / 2 / rate) * 1000
  }

  /** Send a chunk live, hold it for the turn's connection, or keep it as pre-roll. */
  private routeChunk(pcm: ArrayBuffer): void {
    if (this._dropNext) {
      this._dropNext = false
      return
    }
    if (this._liveTo) {
      this._liveTo.sendAudio(pcm)
      return
    }
    const ms = this.chunkMs(pcm)
    const queue = this._queue
    if (queue) {
      // A turn is collecting: past the cap (or sealed) a chunk is dropped, never pre-roll
      if (!queue.sealed && queue.ms + ms <= TURN_QUEUE_MAX_MS) {
        queue.chunks.push(pcm)
        queue.ms += ms
      }
      return
    }
    if (this._isOpenMicActive) {
      this._preRoll.push({ pcm, ms })
      let total = this._preRoll.reduce((sum, c) => sum + c.ms, 0)
      while (this._preRoll.length > 1 && total - this._preRoll[0].ms >= PRE_ROLL_MS) {
        total -= this._preRoll.shift()!.ms
      }
    }
  }

  /** Open a turn's queue with the audio it starts from; nothing is live until it is flushed. */
  private beginQueue(head: ArrayBuffer[]): void {
    this._queue = {
      chunks: [...head],
      ms: head.reduce((sum, c) => sum + this.chunkMs(c), 0),
      sealed: false,
    }
    this._liveTo = null
  }

  private takePreRoll(): ArrayBuffer[] {
    const chunks = this._preRoll.map((c) => c.pcm)
    this._preRoll = []
    return chunks
  }

  // =========================================================================
  // Private — Deepgram STT
  // =========================================================================

  private async connectDeepgram(token: string): Promise<void> {
    // One connection at a time; events from any provider but the current one are ignored.
    // The turn's queue stays: it holds the audio this connection will open with.
    this.closeProvider()
    let provider: STTProvider | null = null
    const current = (): boolean => provider !== null && provider === this.sttProvider
    const sttEvents: STTEvents = {
      onTranscript: (text, isFinal, speechFinal, fromFinalize) => {
        if (!current()) return
        this.handleTranscript(text, isFinal, speechFinal, fromFinalize)
      },
      onUtteranceEnd: () => {
        if (!current()) return
        this.handleUtteranceEnd()
      },
      onError: (error) => {
        if (!current()) return
        if (this._turnOwner === 'person') {
          this.clearTurnTimers()
          this._ending = false
          this.closeSTT()
          this.suspendQuietly()
        }
        this.callbacks.onError(error)
        // In open-mic mode, recover: the turn lets go and the VAD listens again
        if (this._isOpenMicActive) {
          this.endQuietly()
        } else {
          this.setState('error')
        }
      },
      onOpen: () => {
        // Connection ready
      },
      onClose: () => {
        // STT connection closed
      },
    }

    // Select STT provider based on config
    provider = this.deps.createSTTProvider(sttEvents, this.config)
    this.sttProvider = provider

    const sttConfig: STTConfig = {
      token,
      model: this.config.sttModel,
      language: this.config.sttLanguage,
      interimResults: true,
      endpointingMs: 300,
      utteranceEndMs: 1000,
      encoding: 'linear16',
      sampleRate: this.audioCapture?.sampleRate ?? 16000,
      ...(this.config.localStt ? { local: this.config.localStt } : {}),
    }

    await provider.connect(sttConfig)

    // Flush the queue and go live in one step, unless a newer connection replaced this one
    if (!current()) return
    const queue = this._queue
    this._flushedMs = queue?.ms ?? 0
    for (const pcm of queue?.chunks ?? []) provider.sendAudio(pcm)
    this._queue = null
    if (!queue?.sealed) this._liveTo = provider
  }

  /** Close the connection but keep the turn's queue. */
  private closeProvider(): void {
    if (this.sttProvider) {
      this.sttProvider.close()
      this.sttProvider = null
    }
    this._liveTo = null
  }

  private closeSTT(): void {
    this.closeProvider()
    this._queue = null
  }

  // =========================================================================
  // Private — Transcript Handling
  // =========================================================================

  private handleTranscript(
    text: string,
    isFinal: boolean,
    speechFinal: boolean,
    fromFinalize: boolean
  ): void {
    if (isFinal && text) {
      // Accumulate final transcript segments
      this._finalTranscript = this._finalTranscript
        ? `${this._finalTranscript} ${text}`
        : text
      this._partialTranscript = this._finalTranscript
    } else if (text) {
      // Show interim results appended to any accumulated finals
      this._partialTranscript = this._finalTranscript
        ? `${this._finalTranscript} ${text}`
        : text
    }

    this.callbacks.onPartialTranscript(this._partialTranscript)

    if (text && this._turnOwner === 'person' && this._state === 'listening' && !this._ending) {
      this.armSilenceCeiling()
    }
    if (text && this._turnOwner === 'detector' && this._state === 'listening') {
      this.armNoWordsLimit(this._turnSeq)
    }

    if (this._turnOwner === 'person') {
      // The person ends the turn: only the marked answer to Finalize submits
      if (this._ending && fromFinalize) {
        this.processFinalTranscript()
      }
      return
    }

    // speechFinal means end of utterance — time to process
    if (speechFinal && this._finalTranscript) {
      this.processFinalTranscript()
    }
  }

  private handleUtteranceEnd(): void {
    if (this._turnOwner === 'person') {
      if (
        this._ending &&
        !this._releasedEarly &&
        this._state === 'listening' &&
        this.sttProvider &&
        !this.sttProvider.alwaysAnswersFinalize
      ) {
        this.processFinalTranscript()
      }
      return
    }

    // The utterance is over: submit what was heard, or let a sound with no words go
    if (this._turnOwner === 'detector' && this._state === 'listening') {
      this.submitOrEndQuietly()
    }
  }

  /** End an open-mic turn: submit when final text was heard, else end it quietly. */
  private submitOrEndQuietly(): void {
    if (this._finalTranscript.trim()) {
      this.processFinalTranscript()
    } else {
      this.endQuietly()
    }
  }

  private processFinalTranscript(): void {
    // The turn is over: none of its timers may fire into the next one
    this.clearTurnTimers()
    if (this._turnOwner === 'person') this._ending = false
    const transcript = this._finalTranscript.trim()
    if (!transcript) {
      this.closeSTT()
      // No transcript — return to appropriate state
      if (this._isOpenMicActive) {
        this.setState('listening')
        this.resumeVAD()
      } else {
        this.setState('idle')
      }
      return
    }

    // Close STT connection immediately (cost management)
    this.closeSTT()

    // Clear speech detection state
    if (this._isSpeechDetected) {
      this._isSpeechDetected = false
      this.callbacks.onSpeechDetected(false)
    }

    this.setState('processing')
    this.callbacks.onFinalTranscript(transcript)
  }

  // =========================================================================
  // Private — VAD (Open-Mic)
  // =========================================================================

  private handleVADSpeechStart(): void {
    if (this._destroyed || !this._isOpenMicActive) return

    // Barge-in: speech detected while AI is speaking
    if (this._state === 'speaking') {
      // Check AEC cooldown
      if (Date.now() < this._bargeInCooldownUntil) {
        return // Ignore — likely echo from TTS audio
      }

      // Interrupt TTS immediately (tells parent to stop playback + cancel RAG stream)
      this.callbacks.onInterruptTTS()
      // Transition to listening so STT transcript handling works correctly
      this.setState('listening')
    }

    // Only proceed if we're in a state where we can start a new turn
    if (this._state !== 'listening') {
      return
    }

    // A new turn: an await that wakes into a later turn does nothing
    const turn = ++this._turnSeq
    this.clearTurnTimers()

    // Signal speech detection to UI
    this._isSpeechDetected = true
    this.callbacks.onSpeechDetected(true)

    // Reset turn state for new turn
    this._turnOwner = 'detector'
    this.resetTurnState()
    this.closeSTT()
    this.beginQueue(this.takePreRoll())

    // Pause VAD while STT is active (Deepgram handles endpointing now)
    this.vadService?.pause()

    // The limit is read once per turn, so a change applies from the next speech start
    const configured = this.config.openMicNoWordsMs
    this._turnNoWordsMs =
      typeof configured === 'number' && Number.isFinite(configured) && configured > 0
        ? configured
        : OPEN_MIC_NO_WORDS_MS
    this.armNoWordsLimit(turn)

    // Any string is a token, the empty one included (edge-local); only null means none cached
    if (this._cachedToken !== null) {
      void this.connectDeepgramForOpenMic(this._cachedToken, turn)
      return
    }
    // No cached token — fetch one (adds latency, but better than failing)
    void this.refreshToken().then(() => {
      if (turn !== this._turnSeq) return
      if (this._cachedToken === null) {
        this.endQuietly()
      } else {
        void this.connectDeepgramForOpenMic(this._cachedToken, turn)
      }
    })
  }

  private async connectDeepgramForOpenMic(token: string, turn: number): Promise<void> {
    try {
      await this.connectDeepgram(token)
    } catch (error) {
      // The turn is already over (a newer turn, cancel, interrupt, stopOpenMic or destroy closed this connect)
      if (turn !== this._turnSeq) return
      this.callbacks.onError(
        error instanceof Error ? error : new Error(String(error))
      )
      this.endQuietly()
      return
    }
    // State stays 'listening'. The connection is open: the provider gets a full limit
    // to answer the queued audio.
    if (turn === this._turnSeq) this.armNoWordsLimit(turn)
  }

  private handleVADSpeechEnd(): void {
    if (this._destroyed || !this._isOpenMicActive) return

    // A turn still fetching its token or connecting: its queue holds the speech, which the
    // connection hears when it opens; the no-words limit still ends a wordless one
    if (this.detectorTurnOpen() && !this.sttProvider?.isConnected) return

    this._speechEndedAt = Date.now()

    // Signal speech ended to UI
    this._isSpeechDetected = false
    this.callbacks.onSpeechDetected(false)

    // Finalize Deepgram to get remaining transcript
    if (this.sttProvider?.isConnected) {
      this.sttProvider.finalize()

      // Edge-local: no timer ends a turn; the edge server's answer does
      if (this.config.sttProvider === 'whisper-local') return

      // Safety timeout, for this turn only — if no final transcript arrives, let the turn go
      const turn = this._turnSeq
      if (this._speechEndTimer) clearTimeout(this._speechEndTimer)
      this._speechEndTimer = setTimeout(() => {
        this._speechEndTimer = null
        if (turn === this._turnSeq && this._state === 'listening' && this._isOpenMicActive) {
          this.submitOrEndQuietly()
        }
      }, SPEECH_END_TIMEOUT_MS)
    } else {
      // No turn in progress (the AI may be speaking) — just resume VAD, leaving the state alone
      this.resumeVAD()
    }
  }

  /** An open-mic turn is under way: started by the detector, listening, with a queue or a connection. */
  private detectorTurnOpen(): boolean {
    return (
      this._isOpenMicActive &&
      this._turnOwner === 'detector' &&
      this._state === 'listening' &&
      (this.sttProvider !== null || this._queue !== null)
    )
  }

  /**
   * End an open-mic turn with nothing submitted and no error: close STT and drop the
   * queue, clear the preview, stay listening and resume the VAD.
   */
  private endQuietly(): void {
    if (!this._isOpenMicActive) return
    this._turnSeq++
    this.clearTurnTimers()
    this.closeSTT()
    if (this._isSpeechDetected) {
      this._isSpeechDetected = false
      this.callbacks.onSpeechDetected(false)
    }
    this._partialTranscript = ''
    this.callbacks.onPartialTranscript('')
    this._turnOwner = null
    this.setState('listening')
    void this.resumeVAD()
  }

  private async resumeVAD(): Promise<void> {
    if (this._isOpenMicActive && this.vadService && !this._destroyed) {
      try {
        await this.vadService.start()
      } catch {
        // VAD resume failed — not critical, will retry on next cycle
      }
    }
  }

  // =========================================================================
  // Private — Token Management (Open-Mic)
  // =========================================================================

  private async refreshToken(): Promise<void> {
    if (!this._tokenFetcher || this._destroyed) return
    try {
      this._cachedToken = await this._tokenFetcher()
    } catch (error) {
      // Token refresh failed — we'll try again at the next interval
      // or when the next speech start event fires
      console.debug('[VoiceEngine] Token refresh failed:', error)
    }
  }
}
